import { useEffect, useState } from 'react';

import { useActivityClient, ActivityItem } from '@atlassian/recent-work-client';

import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

const GRAPHQL_GATEWAY_URL =
	process.env['NODE_ENV'] === 'production'
		? 'https://www.atlassian.com/gateway/api/graphql'
		: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql';

const START_URL =
	process.env['NODE_ENV'] === 'production'
		? 'https://start.atlassian.com'
		: 'https://start.stg.atlassian.com';

/**
 * How to build a site's deep link. Products differ in whether they bounce
 * through a login endpoint: Jira bounces `/login.jsp` -> `/browse`, while
 * Confluence lands directly on `/wiki` (no login bounce), matching the
 * canonical ReturnToProduct.getProductUrl convention used elsewhere in WAC.
 * The host always comes from the real site URL, so staging
 * (`*.jira-dev.com`) and production (`*.atlassian.net`) are handled
 * implicitly without any environment-specific branching.
 */
export interface SiteLogin {
	/** Pathname the user ultimately lands on, e.g. `/browse` (Jira) or `/wiki` (Confluence). */
	destinationPath: string;
	/**
	 * Optional login endpoint to bounce through, e.g. `/login.jsp`. When set, the URL
	 * becomes `<site>{loginPath}?os_destination=<site>{destinationPath}`. When omitted,
	 * the URL is simply `<site>{destinationPath}` (no login bounce, no search params).
	 */
	loginPath?: string;
}

/** Default preserves the historical Jira behaviour for existing callers. */
const DEFAULT_SITE_LOGIN: SiteLogin = {
	loginPath: '/login.jsp',
	destinationPath: '/browse',
};

export const constructLoginUrl = (url: string, siteLogin: SiteLogin = DEFAULT_SITE_LOGIN) => {
	const loginUrl = new URL(url);

	// No login bounce: land directly on the destination path (e.g. Confluence `/wiki`).
	if (!siteLogin.loginPath) {
		loginUrl.pathname = siteLogin.destinationPath;
		return loginUrl;
	}

	// Login bounce: `<site>{loginPath}?os_destination=<site>{destinationPath}` (e.g. Jira).
	loginUrl.pathname = siteLogin.loginPath;
	const searchParam = new URLSearchParams();
	const destinationUrl = new URL(loginUrl);
	destinationUrl.pathname = siteLogin.destinationPath;
	searchParam.append('os_destination', destinationUrl.href);
	loginUrl.search = searchParam.toString();
	return loginUrl;
};

export interface UserProfileExistingSites {
	cloudId: string;
	displayName: string;
	url: string;
	hasAdminAccess: boolean;
	siteCreatedAt: number;
	products: {
		productName: string;
		productKey: string;
		productEdition: string;
	}[];
}

const filterSitesByProductKey = (
	sites: UserProfileExistingSites[] | undefined,
	productKeyFilter: string,
): UserProfileExistingSites[] => {
	if (sites) {
		const filteredSites =
			sites.filter((site) => {
				return site.products.some((product) => {
					return product.productKey.includes(productKeyFilter);
				});
			}) || [];

		return filteredSites;
	} else {
		return [];
	}
};

export interface UseRecentProductSiteOptions {
	productKeyFilter: string;
	activityClientProduct?: string;
	/**
	 * How to build the site login deep link. Defaults to the Jira form
	 * (`/login.jsp` -> `/browse`) so existing callers are unchanged.
	 */
	siteLogin?: SiteLogin;
}

export const useRecentProductSite = ({
	productKeyFilter,
	activityClientProduct = 'jira',
	siteLogin = DEFAULT_SITE_LOGIN,
}: UseRecentProductSiteOptions) => {
	const { profile, user } = useUserContext();

	const splitName = user.name?.split(' ');

	// Invoked for its hook side-effects/ordering; the client handle itself is unused here.
	useBxpWebStargateClient();
	const activityClient = useActivityClient(
		'v3',
		'www.atlassian.com',
		undefined,
		GRAPHQL_GATEWAY_URL,
	);

	const availableProductSites = filterSitesByProductKey(
		profile.attributes?.existingSites,
		productKeyFilter,
	);

	const hasMultipleSites = availableProductSites.length > 1;

	const [siteName, setSiteName] = useState('');
	const [siteUrl, setSiteUrl] = useState('');
	const [siteInitials, setSiteInitials] = useState('');
	const [cloudSiteId, setCloudSiteId] = useState<string | undefined>();

	useEffect(() => {
		const promises = [];
		let mostRecentActivity: ActivityItem | undefined;
		const fetchActivitiesPromise = activityClient
			.fetchActivities(
				[50, 125, 250],
				{
					limit: 5,
					products: [activityClientProduct],
				},
				['workedOn', 'viewed'],
			)
			.then((data) => {
				// Filter activities to only include those from available product sites
				const filteredViewed = data.viewed.filter((activity) =>
					availableProductSites.some(
						(site) => site.cloudId === activity.object.rootContainerIdentifier.id,
					),
				);
				const filteredWorkedOn = data.workedOn.filter((activity) =>
					availableProductSites.some(
						(site) => site.cloudId === activity.object.rootContainerIdentifier.id,
					),
				);

				const mostRecentlyViewed = filteredViewed[0];
				const mostRecentlyWorkedOn = filteredWorkedOn[0];

				if (mostRecentlyViewed && mostRecentlyWorkedOn) {
					mostRecentActivity =
						new Date(mostRecentlyViewed.timestamp) > new Date(mostRecentlyWorkedOn.timestamp)
							? mostRecentlyViewed
							: mostRecentlyWorkedOn;
				} else if (mostRecentlyWorkedOn) {
					mostRecentActivity = mostRecentlyWorkedOn;
				} else if (mostRecentlyViewed) {
					mostRecentActivity = mostRecentlyViewed;
				}
			})
			.catch((error) => console.log(error));
		promises.push(fetchActivitiesPromise);

		Promise.allSettled(promises).then(() => {
			if (mostRecentActivity) {
				const loginUrl = constructLoginUrl(mostRecentActivity.object.url, siteLogin);
				setSiteUrl(loginUrl.href);
				setSiteName(loginUrl.hostname.split('.')[0] || '');
				setSiteInitials(
					loginUrl.hostname.slice(0, 1).toUpperCase() + loginUrl.hostname.slice(1, 2),
				);
				setCloudSiteId(mostRecentActivity.object.rootContainerIdentifier.id);
			} else if (availableProductSites[0]) {
				const loginUrl = constructLoginUrl(availableProductSites[0].url, siteLogin);
				setSiteUrl(loginUrl.href);
				setSiteName(loginUrl.hostname.split('.')[0] || '');
				setSiteInitials(
					loginUrl.hostname.slice(0, 1).toUpperCase() + loginUrl.hostname.slice(1, 2),
				);
				setCloudSiteId(availableProductSites[0].cloudId);
			} else {
				setSiteUrl(START_URL);
				setSiteName('');
			}
		});
	}, []);

	return {
		splitName,
		hasMultipleSites,
		siteName,
		siteUrl,
		siteInitials,
		cloudSiteId,
	};
};
